import { useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Download } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';

const StudentIDTemplate = ({
    student = {},
    schoolName = 'Zindua School',
    idNumber = '',
    photoUrl = '',
    pathwayTitle = '',
    issuedOn = '',
    validTill = '',
    autoDownload = false,
}) => {
    useEffect(() => {
        if (autoDownload) {
            const timer = setTimeout(() => window.print(), 500);
            return () => clearTimeout(timer);
        }
    }, [autoDownload]);

    return (
        <>
            <style>{`
                @media print {
                    @page { size: A4; margin: 10mm; }
                    body { margin: 0; padding: 0; }
                    .student-id-container, .student-id-container * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                }
                .student-id-container { width: 640px; max-width: 100%; margin: 0 auto; background: #ffffff; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; font-family: Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial; }
                .student-id-head { padding: 16px; display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #e6e6e6; }
                .student-id-body { display:flex; gap:16px; padding:20px; }
                .student-photo { width:140px; height:180px; background:#f3f4f6; border-radius:6px; object-fit:cover; border:1px solid #d1d5db; }
                .student-meta { flex:1; display:flex; flex-direction:column; justify-content:space-between; }
                .meta-top { display:flex; justify-content:space-between; gap:12px; }
                .meta-left { display:flex; flex-direction:column; gap:6px; }
                .student-name { font-size:20px; font-weight:700; color:#0f172a; }
                .student-field { font-size:13px; color:#374151; }
                .id-badge { margin-top:8px; display:inline-block; }
                .student-footer { padding:12px 20px; border-top:1px solid #e6e6e6; font-size:12px; color:#6b7280; text-align:center; }
            `}</style>

            <div className="student-id-container">
                <div className="student-id-head">
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{schoolName}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Student Identification</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <img src="/Zindua Favicon.png" alt="logo" style={{ width: 56, height: 56, objectFit: 'contain' }} />
                    </div>
                </div>

                <div className="student-id-body">
                    <div>
                        <img
                            src={photoUrl || '/default-avatar.png'}
                            alt={student.name || 'Student Photo'}
                            className="student-photo"
                        />
                    </div>

                    <div className="student-meta">
                        <div className="meta-top">
                            <div className="meta-left">
                                <div className="student-name">{student.name || 'Student Name'}</div>
                                <div className="student-field">
                                    Pathway: <span style={{ fontWeight: 600 }}>{pathwayTitle || '—'}</span>
                                </div>
                                <div className="student-field">
                                    ID:{' '}
                                    <span
                                        style={{
                                            fontWeight: 600,
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                                        }}
                                    >
                                        {idNumber || '—'}
                                    </span>
                                </div>
                                <div className="student-field">
                                    Issued On: <span style={{ fontWeight: 600 }}>{issuedOn || '—'}</span>
                                </div>
                                <div className="student-field">
                                    Valid Till: <span style={{ fontWeight: 600 }}>{validTill || '—'}</span>
                                </div>
                            </div>

                            <div style={{ textAlign: 'right' }}>
                                <Badge className="id-badge">Student</Badge>
                                <div style={{ marginTop: 12, fontSize: 12, color: '#4b5563' }}>{schoolName}</div>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>www.zinduaschool.com</div>
                            </div>
                        </div>

                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12, color: '#374151' }}>
                                This card verifies the identity of the bearer for access to Zindua School services.
                            </div>
                        </div>
                    </div>
                </div>

                <div className="student-footer print:block">
                    {schoolName} • Issued {issuedOn} • Valid Till {validTill}
                </div>
            </div>
        </>
    );
};

const StudentIDPage = ({ autoDownload = false }) => {
    const { user } = useUser() || {};

    const student = {
        name: user?.fullName || `${user?.firstName || 'Student'} ${user?.lastName || ''}`.trim() || 'Student',
        email: user?.primaryEmailAddress?.emailAddress || '',
    };

    const idNumber = user?.id ? String(user.id).slice(0, 10) : '0000000000';
    const photoUrl = user?.profileImageUrl || user?.imageUrl || '/default-avatar.png';
    const issuedOn = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const validTill = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return (
        <div className="w-full p-6">
            <div className="flex justify-end mb-4 print:hidden">
                <Button variant="outline" onClick={() => window.print()} className="flex items-center gap-2">
                    <Download size={14} />
                    Download / Print ID
                </Button>
            </div>

            <StudentIDTemplate
                student={student}
                schoolName={'Zindua School'}
                idNumber={idNumber}
                photoUrl={photoUrl}
                pathwayTitle={''}
                issuedOn={issuedOn}
                validTill={validTill}
                autoDownload={autoDownload}
            />
        </div>
    );
};

export default StudentIDPage;
